import React, { useState, useEffect, useContext } from 'react';
import { TablePaginationConfig, message } from 'antd';
import { useIntl } from 'react-intl';
import { ParsedUrlQuery } from 'querystring';
import { JobType } from 'Common/types';
import { downloadFile } from 'ShorePay/utils';
import { normalizeTemplateFileUrl } from 'utils/api';
import { PusherContext } from 'utils/PusherProvider';
import { usePrevious } from 'ahooks';
import {
  GiftcardReport as GiftcardReportType,
  GiftcardReportTableParams,
  PusherJobReport,
} from 'utils/apiClient/services/reports/types';
import {
  useCreateGiftCardReport,
  useGiftcardReport,
  useTaxes,
} from '../hooks/giftcardReport';

import GiftcardReportTable from './views/GiftcardReportTable';
import GiftcardReportDetails from './views/GiftcardReportDetails';
import GiftcardReportHeader from './views/GiftcardReportHeader';

export type GiftcardsProps = {
  query: ParsedUrlQuery;
  currency: string;
};

const GiftcardsReport: React.FC<GiftcardsProps> = ({ query, currency }) => {
  const [visibleReport, setVisibleReport] = useState<GiftcardReportType | null>(
    null
  );
  const taxesQuery = useTaxes({ enabled: query._ === '3' });
  const [searchQuery, setSearchQuery] = useState<string | null>('');
  const previousSearchQuery = usePrevious(searchQuery);
  const [tableParams, setTableParams] = useState<GiftcardReportTableParams>({
    pagination: {
      current: 1,
      pageSize: 20,
    },
  });
  const intl = useIntl();

  const pusherChannel = useContext(PusherContext);

  const [downloadingGCReport, setDownloadingGCReport] = useState(false);
  const createGiftCard = useCreateGiftCardReport();

  const {
    data: giftcardsData,
    isLoading: giftcardsLoading,
    refetch: giftcardsRefetch,
  } = useGiftcardReport({
    tableParams,
    searchQuery: searchQuery ?? '',
    enabled: query._ === '3',
  });

  useEffect(() => {
    if (pusherChannel !== undefined) {
      pusherChannel.bind('event', (data: PusherJobReport) => {
        if (
          data !== undefined &&
          data.fields.type === JobType.GiftCardReport &&
          data.name === 'job.status_update'
        ) {
          if (data.fields.status === 'completed') {
            setDownloadingGCReport(false);
            downloadFile(
              normalizeTemplateFileUrl(data.fields.result.data.file_url),
              'giftcard.xls'
            );
          } else if (data.fields.status === 'failed') {
            setDownloadingGCReport(false);
          }
        }
      });
    }
  }, [pusherChannel, intl, downloadingGCReport]);

  useEffect(() => {
    if (searchQuery !== previousSearchQuery) {
      giftcardsRefetch();
    }
  }, [giftcardsRefetch, searchQuery, previousSearchQuery]);

  const shouldDownloadDisabled = () =>
    (giftcardsData && giftcardsData.count === 0) || giftcardsLoading;

  const onHandleDownload = (action: string) => {
    setDownloadingGCReport(true);
    createGiftCard.mutate(action);
    message.loading(
      intl.formatMessage({
        defaultMessage:
          'The report is being processed and should be available in few instants',
      }),
      2
    );
  };

  const handleTableChange = (params: GiftcardReportTableParams) => {
    setTableParams((state: GiftcardReportTableParams) => ({
      ...state,
      pagination: {
        ...(params.pagination as TablePaginationConfig),
      },
    }));
  };

  return (
    <>
      <GiftcardReportHeader
        searchQuery={searchQuery}
        handleDownload={onHandleDownload}
        downloadInProgress={downloadingGCReport}
        isDisabled={shouldDownloadDisabled()}
        handleSearchChange={(q) =>
          q.length ? setSearchQuery(q) : setSearchQuery('')
        }
      />
      <GiftcardReportTable
        onReportSelect={setVisibleReport}
        reportData={giftcardsData?.results}
        tableParams={{
          ...tableParams,
          ...(giftcardsData !== undefined &&
            giftcardsData !== null && {
              pagination:
                giftcardsData.count < 10
                  ? false
                  : {
                      ...(tableParams.pagination as TablePaginationConfig),
                      total: giftcardsData.count,
                    },
            }),
        }}
        handleTableChange={handleTableChange}
        currency={currency}
        taxes={taxesQuery?.data ? taxesQuery.data : []}
        isDataLoading={giftcardsLoading}
      />
      {visibleReport && (
        <GiftcardReportDetails
          currency={currency}
          report={visibleReport}
          onClose={() => setVisibleReport(null)}
        />
      )}
    </>
  );
};

export default GiftcardsReport;
